/**
 * KnowledgeQueuePage — /knowledge (WS-4)
 *
 * Admin moderation surface for the cross-user shared knowledge repository.
 *
 * - Paginated list of knowledge units with a status filter (draft / approved /
 *   retired / all — default 'draft', the moderation focus) and an optional
 *   taskType filter.
 * - Row click opens a drawer with full detail (markdown body rendered as
 *   preformatted text, provenance, timestamps).
 * - Drawer actions: Approve (draft → approved, optional expiry), Retire
 *   (→ retired), and inline Edit (title/body/taskType/industry/jurisdiction/
 *   expiry via PUT). All actions are optimistic with Toast feedback.
 * - 'flagged' scrubStatus is surfaced prominently so a moderator double-checks
 *   for PII before approving.
 *
 * Mirrors the reducer + optimistic-action pattern of ModerationQueuePage and
 * the Table + Drawer + Toast layout of JobHistoryPage.
 */

import { useCallback, useEffect, useReducer, useState } from 'react';
import { AlertTriangle, ShieldCheck, ShieldAlert } from 'lucide-react';
import {
  getKnowledgeUnits,
  approveKnowledgeUnit,
  retireKnowledgeUnit,
  updateKnowledgeUnit,
  type KnowledgeUnitAdminDto,
  type KnowledgeStatus,
  type UpdateKnowledgeRequest,
} from '../api/knowledgeClient';
import { Table, type Column } from '../components/Table';
import { Drawer } from '../components/Drawer';
import { Toast, type ToastState } from '../components/Toast';

// ---------------------------------------------------------------------------
// State model
// ---------------------------------------------------------------------------

/** 'all' is a UI-only sentinel mapped to no status filter on the wire. */
type StatusFilter = KnowledgeStatus | 'all';

interface QueueState {
  items: KnowledgeUnitAdminDto[];
  total: number;
  page: number;
  pageSize: number;
  statusFilter: StatusFilter;
  taskTypeFilter: string;
  loading: boolean;
  error: string | null;
  selectedId: string | null;
  /** Per-unit action in-flight: knowledgeUnitId → true */
  actionPending: Record<string, boolean>;
}

type QueueAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; items: KnowledgeUnitAdminDto[]; total: number }
  | { type: 'FETCH_ERROR'; error: string }
  | { type: 'SET_PAGE'; page: number }
  | { type: 'SET_STATUS'; status: StatusFilter }
  | { type: 'SET_TASKTYPE'; taskType: string }
  | { type: 'OPEN'; id: string }
  | { type: 'CLOSE' }
  | { type: 'ACTION_START'; id: string }
  | { type: 'ACTION_DONE'; id: string; unit: KnowledgeUnitAdminDto }
  | { type: 'ACTION_ERROR'; id: string };

const PAGE_SIZE = 25;

const initialState: QueueState = {
  items: [],
  total: 0,
  page: 1,
  pageSize: PAGE_SIZE,
  statusFilter: 'draft',
  taskTypeFilter: '',
  loading: false,
  error: null,
  selectedId: null,
  actionPending: {},
};

function reducer(state: QueueState, action: QueueAction): QueueState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false, items: action.items, total: action.total };
    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.error };
    case 'SET_PAGE':
      return { ...state, page: action.page };
    case 'SET_STATUS':
      return { ...state, statusFilter: action.status, page: 1 };
    case 'SET_TASKTYPE':
      return { ...state, taskTypeFilter: action.taskType, page: 1 };
    case 'OPEN':
      return { ...state, selectedId: action.id };
    case 'CLOSE':
      return { ...state, selectedId: null };
    case 'ACTION_START':
      return { ...state, actionPending: { ...state.actionPending, [action.id]: true } };
    case 'ACTION_DONE': {
      const pending = { ...state.actionPending };
      delete pending[action.id];
      const items = state.items.map((u) =>
        u.knowledgeUnitId === action.id ? action.unit : u,
      );
      return { ...state, actionPending: pending, items };
    }
    case 'ACTION_ERROR': {
      const pending = { ...state.actionPending };
      delete pending[action.id];
      return { ...state, actionPending: pending };
    }
  }
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/** ISO string → value for an <input type="datetime-local"> (local time). */
function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** <input type="datetime-local"> value → ISO UTC string, or null when empty. */
function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: KnowledgeStatus }) {
  const colours: Record<KnowledgeStatus, string> = {
    draft: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
    approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    retired: 'bg-zinc-700/40 text-zinc-400 border-zinc-600/40',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium capitalize ${colours[status]}`}>
      {status}
    </span>
  );
}

function ScrubBadge({ scrubStatus, size = 'sm' }: { scrubStatus: 'clean' | 'flagged'; size?: 'sm' | 'md' }) {
  const iconSize = size === 'md' ? 14 : 11;
  if (scrubStatus === 'flagged') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/15 text-amber-300 text-xs font-semibold">
        <ShieldAlert size={iconSize} aria-hidden="true" />
        Flagged
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-zinc-700 bg-zinc-800 text-zinc-400 text-xs font-medium">
      <ShieldCheck size={iconSize} aria-hidden="true" />
      Clean
    </span>
  );
}

// ---------------------------------------------------------------------------
// Detail / edit drawer
// ---------------------------------------------------------------------------

interface DetailDrawerProps {
  unit: KnowledgeUnitAdminDto | null;
  isPending: boolean;
  onClose: () => void;
  onApprove: (id: string, expiresAtUtc: string | null) => void;
  onRetire: (id: string) => void;
  onSaveEdit: (id: string, patch: UpdateKnowledgeRequest) => Promise<void>;
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span className="text-zinc-500 text-xs">{label}</span>
      <p className="text-zinc-300 text-xs break-words">{value ?? '—'}</p>
    </div>
  );
}

/**
 * Body content, split out so it can be keyed by knowledgeUnitId — remounting on
 * a new target naturally resets all transient edit/approve state (no effect).
 */
function KnowledgeDetailBody({
  unit,
  isPending,
  onApprove,
  onRetire,
  onSaveEdit,
}: {
  unit: KnowledgeUnitAdminDto;
  isPending: boolean;
  onApprove: (id: string, expiresAtUtc: string | null) => void;
  onRetire: (id: string) => void;
  onSaveEdit: (id: string, patch: UpdateKnowledgeRequest) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approveExpiry, setApproveExpiry] = useState('');

  // Edit form fields
  const [fTaskType, setFTaskType] = useState('');
  const [fIndustry, setFIndustry] = useState('');
  const [fJurisdiction, setFJurisdiction] = useState('');
  const [fTitle, setFTitle] = useState('');
  const [fBody, setFBody] = useState('');
  const [fExpiry, setFExpiry] = useState('');

  function beginEdit() {
    setFTaskType(unit.taskType);
    setFIndustry(unit.industry ?? '');
    setFJurisdiction(unit.jurisdiction ?? '');
    setFTitle(unit.title);
    setFBody(unit.body);
    setFExpiry(isoToLocalInput(unit.expiresAtUtc));
    setEditing(true);
  }

  async function submitEdit() {
    setSaving(true);
    const patch: UpdateKnowledgeRequest = {
      taskType: fTaskType,
      industry: fIndustry.trim() === '' ? null : fIndustry,
      jurisdiction: fJurisdiction.trim() === '' ? null : fJurisdiction,
      title: fTitle,
      body: fBody,
      expiresAtUtc: localInputToIso(fExpiry),
    };
    try {
      await onSaveEdit(unit.knowledgeUnitId, patch);
      setEditing(false);
    } catch {
      // onSaveEdit re-throws on failure ON PURPOSE, so that `setEditing(false)`
      // above is skipped and the moderator keeps their unsaved edits. The page
      // has already surfaced the reason as a toast, so there is nothing further
      // to do here — but the throw has to STOP here. The caller invokes this as
      // `void submitEdit()`, so letting it escape makes every failed save an
      // unhandled promise rejection.
    } finally {
      setSaving(false);
    }
  }

  return (
        <div className="space-y-4">
          {/* Flagged PII warning */}
          {unit.scrubStatus === 'flagged' && (
            <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <ShieldAlert size={16} className="shrink-0 mt-0.5 text-amber-400" aria-hidden="true" />
              <p className="text-xs text-amber-200">
                This unit was flagged by the PII scrubber. Review the body carefully for
                personal or sensitive data before approving.
              </p>
            </div>
          )}

          {!editing ? (
            <>
              {/* Classification */}
              <div className="grid grid-cols-2 gap-3">
                <DetailField label="Task Type" value={unit.taskType} />
                <DetailField label="Usage Count" value={unit.usageCount} />
                <DetailField label="Industry" value={unit.industry} />
                <DetailField label="Jurisdiction" value={unit.jurisdiction} />
                <DetailField
                  label="Quality Score"
                  value={unit.qualityScore ?? '—'}
                />
                <DetailField label="Version" value={`v${unit.version}`} />
              </div>

              {/* Body */}
              <div>
                <p className="text-xs font-semibold text-zinc-400 mb-1">Body</p>
                <pre className="text-xs text-zinc-300 bg-zinc-950 rounded p-3 overflow-x-auto whitespace-pre-wrap break-words font-mono max-h-96 overflow-y-auto">
                  {unit.body}
                </pre>
              </div>

              {/* Provenance */}
              <div className="border-t border-zinc-800 pt-3 grid grid-cols-2 gap-3">
                <DetailField
                  label="Proposed By Model"
                  value={unit.proposedByModel}
                />
                <DetailField
                  label="Source Job IDs"
                  value={
                    unit.sourceJobIds ? (
                      <span className="font-mono break-all">{unit.sourceJobIds}</span>
                    ) : (
                      '—'
                    )
                  }
                />
                <DetailField label="Created" value={formatDate(unit.createdAtUtc)} />
                <DetailField label="Updated" value={formatDate(unit.updatedAtUtc)} />
                <DetailField label="Approved" value={formatDate(unit.approvedAtUtc)} />
                <DetailField label="Approved By" value={unit.approvedBy} />
                <DetailField label="Retired" value={formatDate(unit.retiredAtUtc)} />
                <DetailField label="Expires" value={formatDate(unit.expiresAtUtc)} />
              </div>

              {/* Actions */}
              <div className="border-t border-zinc-800 pt-4 space-y-3">
                {unit.status === 'draft' && (
                  <div className="space-y-2">
                    <label className="block text-xs text-zinc-400" htmlFor="approve-expiry">
                      Expiry (optional)
                    </label>
                    <input
                      id="approve-expiry"
                      type="datetime-local"
                      value={approveExpiry}
                      onChange={(e) => setApproveExpiry(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-200 focus:outline-none focus:border-violet-500 min-h-[48px]"
                    />
                    <button
                      type="button"
                      disabled={isPending}
                      onClick={() => onApprove(unit.knowledgeUnitId, localInputToIso(approveExpiry))}
                      className="w-full min-h-[48px] px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium disabled:opacity-50 transition-colors"
                    >
                      Approve
                    </button>
                  </div>
                )}

                {unit.status !== 'retired' && (
                  <button
                    type="button"
                    disabled={isPending}
                    onClick={() => onRetire(unit.knowledgeUnitId)}
                    className="w-full min-h-[48px] px-4 py-2 rounded-lg border border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300 text-sm font-medium disabled:opacity-50 transition-colors"
                  >
                    Retire
                  </button>
                )}

                <button
                  type="button"
                  disabled={isPending}
                  onClick={beginEdit}
                  className="w-full min-h-[48px] px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm font-medium disabled:opacity-50 transition-colors"
                >
                  Edit
                </button>
              </div>
            </>
          ) : (
            /* Edit form */
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submitEdit();
              }}
              className="space-y-3"
            >
              <div>
                <label className="block text-xs text-zinc-400 mb-1" htmlFor="edit-title">Title</label>
                <input
                  id="edit-title"
                  type="text"
                  value={fTitle}
                  onChange={(e) => setFTitle(e.target.value)}
                  className="w-full px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-200 focus:outline-none focus:border-violet-500 min-h-[48px]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-zinc-400 mb-1" htmlFor="edit-tasktype">Task Type</label>
                  <input
                    id="edit-tasktype"
                    type="text"
                    value={fTaskType}
                    onChange={(e) => setFTaskType(e.target.value)}
                    className="w-full px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-200 focus:outline-none focus:border-violet-500 min-h-[48px]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1" htmlFor="edit-expiry">Expiry</label>
                  <input
                    id="edit-expiry"
                    type="datetime-local"
                    value={fExpiry}
                    onChange={(e) => setFExpiry(e.target.value)}
                    className="w-full px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-200 focus:outline-none focus:border-violet-500 min-h-[48px]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1" htmlFor="edit-industry">Industry</label>
                  <input
                    id="edit-industry"
                    type="text"
                    value={fIndustry}
                    onChange={(e) => setFIndustry(e.target.value)}
                    className="w-full px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-200 focus:outline-none focus:border-violet-500 min-h-[48px]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1" htmlFor="edit-jurisdiction">Jurisdiction</label>
                  <input
                    id="edit-jurisdiction"
                    type="text"
                    value={fJurisdiction}
                    onChange={(e) => setFJurisdiction(e.target.value)}
                    className="w-full px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-200 focus:outline-none focus:border-violet-500 min-h-[48px]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-zinc-400 mb-1" htmlFor="edit-body">Body</label>
                <textarea
                  id="edit-body"
                  value={fBody}
                  onChange={(e) => setFBody(e.target.value)}
                  rows={14}
                  className="w-full px-3 py-2 text-xs font-mono bg-zinc-950 border border-zinc-700 rounded text-zinc-200 focus:outline-none focus:border-violet-500 resize-y"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 min-h-[48px] px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-zinc-950 text-sm font-medium disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setEditing(false)}
                  className="flex-1 min-h-[48px] px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-medium disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
  );
}

function KnowledgeDetailDrawer({
  unit,
  isPending,
  onClose,
  onApprove,
  onRetire,
  onSaveEdit,
}: DetailDrawerProps) {
  const title = unit ? (
    <div className="space-y-1 min-w-0">
      <p className="text-sm font-semibold text-zinc-100 truncate" title={unit.title}>
        {unit.title}
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <StatusBadge status={unit.status} />
        <ScrubBadge scrubStatus={unit.scrubStatus} />
        <span className="text-xs text-zinc-500 font-mono">v{unit.version}</span>
      </div>
    </div>
  ) : (
    'Knowledge Unit'
  );

  return (
    <Drawer open={!!unit} onClose={onClose} title={title}>
      {unit && (
        <KnowledgeDetailBody
          key={unit.knowledgeUnitId}
          unit={unit}
          isPending={isPending}
          onApprove={onApprove}
          onRetire={onRetire}
          onSaveEdit={onSaveEdit}
        />
      )}
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const STATUS_FILTERS: StatusFilter[] = ['draft', 'approved', 'retired', 'all'];

export function KnowledgeQueuePage() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [toast, setToast] = useState<ToastState | null>(null);
  const dismissToast = useCallback(() => setToast(null), []);

  // Debounce the free-text taskType filter into the query.
  const [taskTypeInput, setTaskTypeInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      dispatch({ type: 'SET_TASKTYPE', taskType: taskTypeInput.trim() });
    }, 350);
    return () => clearTimeout(t);
  }, [taskTypeInput]);

  const loadPage = useCallback(async () => {
    dispatch({ type: 'FETCH_START' });
    try {
      const result = await getKnowledgeUnits({
        status: state.statusFilter === 'all' ? undefined : state.statusFilter,
        taskType: state.taskTypeFilter || undefined,
        page: state.page,
        pageSize: state.pageSize,
      });
      dispatch({ type: 'FETCH_SUCCESS', items: result.items, total: result.total });
    } catch (err) {
      dispatch({
        type: 'FETCH_ERROR',
        error: err instanceof Error ? err.message : 'Failed to load knowledge units',
      });
    }
  }, [state.statusFilter, state.taskTypeFilter, state.page, state.pageSize]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const handleApprove = useCallback(async (id: string, expiresAtUtc: string | null) => {
    dispatch({ type: 'ACTION_START', id });
    try {
      const updated = await approveKnowledgeUnit(id, { expiresAtUtc });
      dispatch({ type: 'ACTION_DONE', id, unit: updated });
      setToast({ variant: 'success', message: 'Knowledge unit approved' });
    } catch (err) {
      dispatch({ type: 'ACTION_ERROR', id });
      setToast({
        variant: 'error',
        message: err instanceof Error ? err.message : 'Failed to approve unit',
      });
    }
  }, []);

  const handleRetire = useCallback(async (id: string) => {
    dispatch({ type: 'ACTION_START', id });
    try {
      const updated = await retireKnowledgeUnit(id);
      dispatch({ type: 'ACTION_DONE', id, unit: updated });
      setToast({ variant: 'success', message: 'Knowledge unit retired' });
    } catch (err) {
      dispatch({ type: 'ACTION_ERROR', id });
      setToast({
        variant: 'error',
        message: err instanceof Error ? err.message : 'Failed to retire unit',
      });
    }
  }, []);

  const handleSaveEdit = useCallback(async (id: string, patch: UpdateKnowledgeRequest) => {
    dispatch({ type: 'ACTION_START', id });
    try {
      const updated = await updateKnowledgeUnit(id, patch);
      dispatch({ type: 'ACTION_DONE', id, unit: updated });
      setToast({ variant: 'success', message: 'Knowledge unit updated' });
    } catch (err) {
      dispatch({ type: 'ACTION_ERROR', id });
      setToast({
        variant: 'error',
        message: err instanceof Error ? err.message : 'Failed to save changes',
      });
      throw err; // let the drawer keep edit mode open on failure
    }
  }, []);

  const selectedUnit =
    state.selectedId !== null
      ? state.items.find((u) => u.knowledgeUnitId === state.selectedId) ?? null
      : null;

  const columns: Column<KnowledgeUnitAdminDto>[] = [
    {
      key: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'taskType',
      label: 'Task Type',
      render: (row) => <span className="text-xs">{row.taskType}</span>,
    },
    {
      key: 'industry',
      label: 'Industry',
      render: (row) => <span className="text-xs text-zinc-400">{row.industry || '—'}</span>,
    },
    {
      key: 'jurisdiction',
      label: 'Jurisdiction',
      render: (row) => <span className="text-xs text-zinc-400">{row.jurisdiction || '—'}</span>,
    },
    {
      key: 'title',
      label: 'Title',
      render: (row) => (
        <span className="text-xs text-zinc-200 line-clamp-2 max-w-xs" title={row.title}>
          {row.title}
        </span>
      ),
    },
    {
      key: 'qualityScore',
      label: 'Quality',
      render: (row) => (
        <span className="text-xs text-zinc-400">
          {row.qualityScore ?? '—'}
        </span>
      ),
    },
    {
      key: 'scrubStatus',
      label: 'Scrub',
      render: (row) => <ScrubBadge scrubStatus={row.scrubStatus} />,
    },
    {
      key: 'usageCount',
      label: 'Usage',
      render: (row) => <span className="text-xs text-zinc-400">{row.usageCount}</span>,
    },
    {
      key: 'createdAtUtc',
      label: 'Created',
      render: (row) => <span className="text-xs font-mono text-zinc-500">{formatDate(row.createdAtUtc)}</span>,
    },
  ];

  return (
    <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Header + filter bar */}
      <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-zinc-800 bg-zinc-950">
        <div className="mr-2">
          <h1 className="text-sm font-semibold text-zinc-100">Knowledge Moderation</h1>
          <p className="text-xs text-zinc-500">Review shared knowledge units before they go live</p>
        </div>

        <div className="flex items-center gap-1" role="group" aria-label="Filter by status">
          {STATUS_FILTERS.map((s) => {
            const isActive = state.statusFilter === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => dispatch({ type: 'SET_STATUS', status: s })}
                aria-pressed={isActive}
                className={[
                  'px-3 py-1 rounded-full text-xs font-medium border transition-colors min-h-[48px] capitalize',
                  isActive
                    ? 'bg-violet-600 text-zinc-950 border-violet-500'
                    : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:bg-zinc-700',
                ].join(' ')}
              >
                {s}
              </button>
            );
          })}
        </div>

        <input
          type="text"
          value={taskTypeInput}
          onChange={(e) => setTaskTypeInput(e.target.value)}
          placeholder="Filter by taskType…"
          aria-label="Filter by task type"
          className="px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:border-violet-500 min-h-[48px] w-52"
        />

        <span className="ml-auto text-xs text-zinc-500">
          {state.total.toLocaleString()} units
        </span>
      </div>

      {/* Error banner */}
      {state.error && (
        <div className="flex items-center gap-2 px-6 py-2 bg-red-500/10 border-b border-red-500/20 text-red-300 text-xs">
          <AlertTriangle size={14} aria-hidden="true" />
          <span>{state.error}</span>
          <button
            type="button"
            onClick={() => void loadPage()}
            className="underline hover:text-red-100"
          >
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <Table
          columns={columns}
          rows={state.items}
          keyFn={(row) => row.knowledgeUnitId}
          onRowClick={(row) => dispatch({ type: 'OPEN', id: row.knowledgeUnitId })}
          isLoading={state.loading}
          emptyMessage="No knowledge units match this filter."
          page={state.page}
          pageSize={state.pageSize}
          total={state.total}
          onPageChange={(p) => dispatch({ type: 'SET_PAGE', page: p })}
        />
      </div>

      {/* Drawer */}
      <KnowledgeDetailDrawer
        unit={selectedUnit}
        isPending={selectedUnit ? state.actionPending[selectedUnit.knowledgeUnitId] === true : false}
        onClose={() => dispatch({ type: 'CLOSE' })}
        onApprove={handleApprove}
        onRetire={handleRetire}
        onSaveEdit={handleSaveEdit}
      />

      {/* Toast */}
      <Toast toast={toast} onDismiss={dismissToast} />
    </main>
  );
}
